/* eslint-disable react/no-unescaped-entities */
/* eslint-disable max-len */
import currentOrgConfig from '@config/current-org-config';
import { FAQ, OrgConfig } from '@config/types';
import { DATA_TEST_IDS } from '@data-test-ids';
import { Typography } from '@mui/material';

export const customConfigFaq = (): readonly FAQ[] => {
  if (import.meta.env.VITE_NSDC_PARTNER) {
    const org = import.meta.env.VITE_ORG;
    return [
      {
        question: `How long does it take to get my docs verified and sent to <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span>?`,
        answer: `After the successful submission of the documents and successful payment, it may take 2-3 weeks (this excludes weekend and public holidays) to get them verified by your institution. In case the documents submitted from your side are incomplete, then the verification time will increase.`
      },
      {
        question: `Why is the verification fee different for different candidates?`,
        answer: `The verification fee may vary from individual to individual as it depends on the number of documents submitted, as well as on the institution that issued them.`
      },
      {
        question: `What should I do if I don’t receive any of the OTPs?`,
        answer: `Your One Time Password (OTP) is sent to the same email ID used on your <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> application. This may take up to 5 minutes.\n\nIf you have not received your OTP, please check your Spam/Junk folders before trying again. Please ensure you have entered the correct <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> reference number.`
      },
      {
        question: `How many attempts are there to get the OTPs? In case I fail in all the attempts, what should I do next?`,
        answer: `You will be allowed to request up to 3 OTPs.\n\nOn 3 unsuccessful attempts, you will be blocked for 2 hours. You may try again once this period has expired.\n\nIn case you do not receive the OTP over email, check the Spam/Junk folders and check for any email from&nbsp; noreply-nsdctrust@nsdcindia.org\n\nIn case you receive multiple OTPs, use the OTP received most recently. The older OTPs will not work.`
      },
      {
        question: `I don’t want to complete all the verifications, but I can’t see the payment button after completing one verification`,
        answer: `You need to mark them as skipped by clicking the verification and then clicking skip, once done the form will be complete and you will be able to see the payment button.`
      },
      {
        question: `How will <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> receive my documents?`,
        answer: `Once your documents are verified, we send a report directly to <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> for credential evaluation. You will receive a completion email and SMS once your documents are verified. For credential evaluation updates, please contact <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> support.`
      },
      {
        question: `What’s the difference between NSDC and <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span>?`,
        answer: `NSDC has partnered with <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> to digitalize the document verification part of the credential evaluation process. We verify the student's educational documents and send the verified documents directly to <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> for evaluation. <span data-test-id="${DATA_TEST_IDS.ORG_NAME_TEXT}">${org}</span> then evaluates the document to generate the final evaluation report.`
      },
      {
        question: `Do I need to contact my college or pay them any fee?`,
        answer: `No, the charge you pay to NSDC includes that fee and you do not need to pay the university any other amount.`
      },
      {
        question: `What should I do if I realize there’s an error in the documents I uploaded?`,
        answer: `If you haven't completed the payment, you can simply click the verification and re-upload the data and documents. In case the payment is completed, you can contact us via the chatbot or you can write to us on our email&nbsp; trust.support@nsdcindia.org`
      },
      {
        question: `How can I check the progress of my verification request?`,
        answer: `You will be notified on your contact email and phone number via SMS/WhatsApp for any updates on your application. You can also use the chatbot which appears post login to know the status of your application.`
      },
      {
        question: `What happens if my payment fails, or I encounter issues during the payment?`,
        answer: `Check if the details entered are correct and try again. If it still doesn't work, try another payment method. In case the payment gateway is down, please try after some time.`
      },
      {
        question: `What should I do if I experience technical issues with the platform?`,
        answer: `In case you are experiencing any technical issues, you can contact us via the chatbot or you can write to us on our email&nbsp;trust.support@nsdcindia.org`
      },
      {
        question: `How do I contact customer support?`,
        answer: `You can login to our portal and use the chatbot to contact support team or you can write to us on our email&nbsp; trust.support@nsdcindia.org`
      }
    ];
  }
  if (
    !currentOrgConfig ||
    !('FAQ' in currentOrgConfig) ||
    !Array.isArray((currentOrgConfig as OrgConfig).FAQ)
  ) {
    return [];
  }

  return (currentOrgConfig as OrgConfig).FAQ;
};

export const customConfigInstructionsGen = () => {
  if (import.meta.env.VITE_NSDC_PARTNER) {
    return [
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 1: Enter{' '}
            <span data-test-id={DATA_TEST_IDS.ORG_NAME_TEXT}>{import.meta.env.VITE_ORG}</span>{' '}
            "Reference Number"
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                Post login, click the "User Details" and input your "Reference Number"
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 2: Email Authentication Via OTP
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                You will receive a 6-digit One-time-password on email registered with{' '}
                <span data-test-id={DATA_TEST_IDS.ORG_NAME_TEXT}>{import.meta.env.VITE_ORG}</span>.
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 3: Identity Verification - Aadhaar eKYC
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                Enter your 12-digit Aadhaar Number to receive OTP on your registered phone number
              </Typography>
              <Typography component="li" variant="body1">
                Enter the OTP received to verify your Aadhaar
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 4: 10th Marksheet Upload (optional)
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                Enter the details required under the 10th-Certificate section.
              </Typography>
              <Typography component="li" variant="body1">
                Click on the option for uploading your 10th-grade mark sheet.
              </Typography>
              <Typography component="li" variant="body1">
                Upload the scan-copy of your 10th-grade marksheet in PDF Format.
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 5: 12th Marksheet Upload (optional)
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                Similarly, for 12th Certificate enter the details required
              </Typography>
              <Typography component="li" variant="body1">
                Select the file containing your 12th-grade marks and upload it.
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 6: Degree Upload (Provisional/Final Degree Certificate and All Semester
            Marksheets.)
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                Proceed to the section for uploading proof of your degree.
              </Typography>
              <Typography component="li" variant="body1">
                Upload a copy of your degree certificate and marksheet for all semesters.
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 7: Add Multiple Degrees (If Applicable)
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                If you have multiple degrees to add, find the option to "Add Another Degree."
              </Typography>
              <Typography component="li" variant="body1">
                Repeat the process by uploading documents for each additional degree.
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 8: Review Uploaded Documents
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                Ensure that all uploaded documents are accurate and legible.
              </Typography>
              <Typography component="li" variant="body1">
                Review the information associated with each degree.
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Step 9: Proceed to Payment
          </Typography>
        ),
        description: (
          <div>
            <ul>
              <Typography component="li" variant="body1">
                After successfully uploading all required documents, click the "Pay" button at the
                bottom
              </Typography>
              <Typography component="li" variant="body1">
                Follow the prompts to complete the payment process
              </Typography>
              <Typography component="li" variant="body1">
                The prices may vary from individual to individual as it depends on number of
                documents submitted as well as the graduating institute.
              </Typography>
            </ul>
          </div>
        )
      },
      {
        label: (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Contact Us
          </Typography>
        ),
        description: (
          <div>
            <Typography variant="body1">
              Call us at{' '}
              {import.meta.env.VITE_ORG == 'NEU' ? '+1 (650) 305 5384' : '+91-9667766726'} between
              11 AM - 7 PM (Indian Standard Time), Monday to Friday. The provided contact number is
              for calls only and is not available for WhatsApp or SMS communications.
            </Typography>
          </div>
        )
      }
    ];
  }

  if (
    !currentOrgConfig ||
    !('INSTRUCTIONS' in currentOrgConfig) ||
    !Array.isArray((currentOrgConfig as any).INSTRUCTIONS)
  ) {
    return [];
  }

  return (currentOrgConfig as any).INSTRUCTIONS.map((step: any) => {
    const isList = step.description.length > 1;
    const ListTag = step.type === 'ol' ? 'ol' : 'ul';

    return {
      label: (
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {step.label}
        </Typography>
      ),
      description: isList ? (
        <ListTag>
          {step.description.map((text: string, index: number) => (
            <Typography key={index} component="li" variant="body1">
              {text}
            </Typography>
          ))}
        </ListTag>
      ) : (
        <Typography variant="body1">{step.description[0]}</Typography>
      )
    };
  });
};

export const configs = {
  faq: customConfigFaq(),
  instructions: customConfigInstructionsGen()
};
